import { useCallback, useRef } from 'react';
import { BranchStep } from 'dorian-shared/types/branch/BranchStep';
import { HubOption, HubSettings, HUB_MAX_REGULAR_OPTIONS, isContinueOption } from 'dorian-shared/types/branch/HubOption';
import {
  createDefaultContinueOption,
  createDefaultRegularOption,
} from '../hubStepDefaults';
import { cloneWithNewUuids } from '../hubOptionClone';

/** Compute next sortOrder for a new regular option */
function getNextSortOrder(options: HubOption[]): number {
  const regularOptions = options.filter((o) => !isContinueOption(o));
  return regularOptions.reduce((max, o) => Math.max(max, o.sortOrder), -1) + 1;
}

/** Ensure a Continue option always exists in the list */
function ensureContinue(options: HubOption[]): HubOption[] {
  const hasContinue = options.some((o) => isContinueOption(o));
  if (hasContinue) return options;
  return [...options, createDefaultContinueOption()];
}

type UseHubOptionsParams = {
  step: BranchStep;
  stepIndex: number;
  updateStep: (index: number, step: BranchStep) => void;
};

export function useHubOptions({ step, stepIndex, updateStep }: UseHubOptionsParams) {
  const stepRef = useRef(step);
  stepRef.current = step;

  const hubOptions = step.hubOptions ?? [];

  const updateOptions = useCallback((newOptions: HubOption[]) => {
    updateStep(stepIndex, { ...stepRef.current, hubOptions: ensureContinue(newOptions) });
  }, [stepIndex, updateStep]);

  const handleHubSettingsChange = useCallback((newSettings: HubSettings) => {
    updateStep(stepIndex, { ...stepRef.current, hubSettings: newSettings });
  }, [stepIndex, updateStep]);

  const handleAddOption = useCallback(() => {
    const currentOptions = stepRef.current.hubOptions ?? [];
    const regularOptions = currentOptions.filter((o) => !isContinueOption(o));
    if (regularOptions.length >= HUB_MAX_REGULAR_OPTIONS) return;

    const newOption = createDefaultRegularOption(getNextSortOrder(currentOptions));
    updateOptions([...currentOptions, newOption]);
  }, [updateOptions]);

  const handleDeleteOptions = useCallback((uuids: string[]) => {
    const currentOptions = stepRef.current.hubOptions ?? [];
    const uuidSet = new Set(uuids);
    const filtered = currentOptions.filter((o) => !uuidSet.has(o.uuid) || isContinueOption(o));
    updateOptions(filtered);
  }, [updateOptions]);

  const handleUpdateOption = useCallback((uuid: string, changes: Partial<HubOption>) => {
    const currentOptions = stepRef.current.hubOptions ?? [];
    updateOptions(
      currentOptions.map((o) => (o.uuid === uuid ? { ...o, ...changes } : o)),
    );
  }, [updateOptions]);

  const handleDuplicateOption = useCallback((uuid: string) => {
    const currentOptions = stepRef.current.hubOptions ?? [];
    const source = currentOptions.find((o) => o.uuid === uuid);
    if (!source || isContinueOption(source)) return;

    const regularOptions = currentOptions.filter((o) => !isContinueOption(o));
    if (regularOptions.length >= HUB_MAX_REGULAR_OPTIONS) return;

    const duplicate: HubOption = {
      ...cloneWithNewUuids(source),
      sortOrder: getNextSortOrder(currentOptions),
    };
    updateOptions([...currentOptions, duplicate]);
  }, [updateOptions]);

  const handlePasteOptions = useCallback((pastedOptions: HubOption[]): { inserted: number; skipped: number } => {
    const currentOptions = stepRef.current.hubOptions ?? [];
    const regularOptions = currentOptions.filter((o) => !isContinueOption(o));
    const available = HUB_MAX_REGULAR_OPTIONS - regularOptions.length;

    if (available <= 0) {
      return { inserted: 0, skipped: pastedOptions.length };
    }

    const toInsert = pastedOptions.slice(0, available);
    const skipped = pastedOptions.length - toInsert.length;
    const nextSortOrder = getNextSortOrder(currentOptions);
    const withSortOrder = toInsert.map((o, i) => ({
      ...o,
      sortOrder: nextSortOrder + i,
    }));

    updateOptions([...currentOptions, ...withSortOrder]);
    return { inserted: toInsert.length, skipped };
  }, [updateOptions]);

  return {
    hubOptions: ensureContinue(hubOptions),
    hubSettings: step.hubSettings,
    handleHubSettingsChange,
    handleAddOption,
    handleDeleteOptions,
    handleUpdateOption,
    handleDuplicateOption,
    handlePasteOptions,
  };
}
